import { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import {
    Save, Plus, CheckCircle2, AlertCircle, Eye, X, Undo2, History, Send, FileEdit,
} from "lucide-react";
import { api, formatApiError } from "@/lib/api";

/**
 * Batch 22 — Admin Impact Metrics.
 *
 * Enhanced editor for the Community Impact statistics with Draft/Publish
 * workflow, full audit trail, preview modal, controlled prefix/suffix
 * fields, validation and a shared source-of-truth (`impact_stats`).
 *
 * The public homepage and Impact Hub read the same collection, so publishing
 * here immediately updates the live site without any code change.
 */
const PREFIX_OPTIONS = ["", "US$", "USD ", "$", "ZWL$", "R"];
const SUFFIX_OPTIONS = ["", "+", "%", " lives", " projects", " families",
    " households", " people", " youth", " women", " devices", " M", " K"];

const BLANK = () => ({
    key: "",
    title: "",
    label: "",
    description: "",
    value: 0,
    prefix: "",
    suffix: "+",
    order: 99,
    verified: false,
    active: true,
    status: "draft",
    _draft: true,
});

export default function AdminImpactMetrics() {
    const [rows, setRows] = useState([]);
    const [busy, setBusy] = useState(false);
    const [preview, setPreview] = useState(null);
    const [historyFor, setHistoryFor] = useState(null);
    const [dirty, setDirty] = useState({});

    const load = useCallback(async () => {
        try {
            const { data } = await api.get("/admin/impact-stats");
            const items = (data.stats || []).map((s) => ({
                prefix: "", suffix: "+", title: s.title || s.label || s.key,
                description: "", active: s.active !== false,
                status: s.verified ? "published" : "draft",
                ...s,
            }));
            setRows(items);
            setDirty({});
        } catch (e) {
            toast.error(formatApiError(e));
        }
    }, []);
    useEffect(() => { load(); }, [load]);

    function markDirty(i) { setDirty((d) => ({ ...d, [i]: true })); }
    function update(i, field, value) {
        setRows((s) => s.map((r, idx) => (idx === i ? { ...r, [field]: value } : r)));
        markDirty(i);
    }
    function add() {
        setRows((s) => [...s, BLANK()]);
        setDirty((d) => ({ ...d, [rows.length]: true }));
    }
    function cancel(i) {
        const row = rows[i];
        if (row._draft) { setRows((s) => s.filter((_, idx) => idx !== i)); }
        else { load(); }
    }

    async function persist(i, { publish, note = "" }) {
        const row = rows[i];
        if (!row.key || !row.key.trim()) return toast.error("Metric key is required");
        if (row.value < 0) return toast.error("Value must be zero or greater");
        setBusy(true);
        try {
            const body = {
                key: row.key.trim().toLowerCase(),
                title: row.title || row.label || row.key,
                label: row.label || row.title || row.key,
                description: row.description || "",
                value: Number(row.value) || 0,
                prefix: row.prefix || "",
                suffix: row.suffix || "",
                order: Number(row.order) || 0,
                verified: !!publish,
                active: row.active !== false,
                note,
            };
            const { data } = await api.post("/admin/impact-stats", body);
            toast.success(publish ? "Published — public site now shows the new value." : "Saved as draft.");
            // Merge server response into local state so subsequent saves reference the same key
            setRows((s) => s.map((r, idx) => (idx === i ? { ...r, ...data.stat, _draft: false } : r)));
            setDirty((d) => { const copy = { ...d }; delete copy[i]; return copy; });
            load();
        } catch (e) {
            toast.error(formatApiError(e));
        } finally { setBusy(false); }
    }

    async function openHistory(row) {
        try {
            const { data } = await api.get(`/admin/impact-stats/${encodeURIComponent(row.key)}/history`);
            setHistoryFor({ row, history: data.history || [] });
        } catch (e) { toast.error(formatApiError(e)); }
    }

    const publishedCount = useMemo(() => rows.filter((r) => r.verified && r.active !== false).length, [rows]);

    return (
        <div data-testid="admin-impact-metrics">
            <div className="gsf-eyebrow">Impact</div>
            <div className="flex flex-wrap items-end justify-between gap-3 mt-2">
                <div>
                    <h1 className="font-serif text-3xl text-[color:var(--gsf-green-700)] font-bold" data-testid="impact-metrics-title">
                        Community Impact Metrics
                    </h1>
                    <p className="mt-3 text-sm text-gray-600 max-w-2xl">
                        Publish updates to every Community Impact figure without editing code. The public
                        website and generated Impact Snapshot reports both read from this single source.
                        Only <strong>published</strong> and <strong>active</strong> metrics appear publicly;
                        drafts stay visible here.
                    </p>
                </div>
                <div className="text-xs text-gray-500 flex items-center gap-4">
                    <span data-testid="published-count">Published: <strong className="text-[color:var(--gsf-green-700)]">{publishedCount}</strong></span>
                    <button onClick={add} className="gsf-btn-outline text-sm" data-testid="metric-add">
                        <Plus size={14} /> Add metric
                    </button>
                </div>
            </div>

            <div className="mt-6 space-y-4">
                {rows.map((row, i) => (
                    <MetricEditor
                        key={`${row.key}-${i}`}
                        i={i}
                        row={row}
                        dirty={!!dirty[i]}
                        busy={busy}
                        onChange={update}
                        onPreview={() => setPreview(row)}
                        onHistory={() => openHistory(row)}
                        onCancel={() => cancel(i)}
                        onSaveDraft={(note) => persist(i, { publish: false, note })}
                        onPublish={(note) => persist(i, { publish: true, note })}
                    />
                ))}
                {rows.length === 0 && (
                    <div className="gsf-card p-8 text-center text-sm text-gray-500" data-testid="metrics-empty">
                        No metrics yet. Click <em>Add metric</em> to create one.
                    </div>
                )}
            </div>

            {preview && <PreviewModal metric={preview} onClose={() => setPreview(null)} />}
            {historyFor && <HistoryModal payload={historyFor} onClose={() => setHistoryFor(null)} />}
        </div>
    );
}

function MetricEditor({ i, row, dirty, busy, onChange, onPreview, onHistory, onCancel, onSaveDraft, onPublish }) {
    const [note, setNote] = useState("");
    const isPublished = row.verified && row.active !== false;
    const displayed = `${row.prefix || ""}${Number(row.value || 0).toLocaleString()}${row.suffix || ""}`;
    const invalidKey = row.key && !/^[a-z0-9][a-z0-9_]{1,39}$/.test(row.key);

    return (
        <div className="gsf-card p-6" data-testid={`metric-editor-${row.key || `new-${i}`}`}>
            <div className="flex flex-wrap items-start justify-between gap-3">
                <div>
                    <div className="flex items-center gap-3 flex-wrap">
                        <h2 className="font-serif text-xl text-[color:var(--gsf-green-700)] font-semibold" data-testid={`metric-heading-${i}`}>
                            {row.title || row.label || row.key || "Untitled metric"}
                        </h2>
                        <span className={`text-xs font-medium inline-flex items-center gap-1 rounded-full px-2.5 py-0.5 ${isPublished ? "bg-[color:var(--gsf-soft-green)] text-[color:var(--gsf-green-700)]" : "bg-amber-50 text-amber-700"}`}
                              data-testid={`metric-status-${i}`}>
                            {isPublished ? <><CheckCircle2 size={12} /> Published</> : <><FileEdit size={12} /> Draft</>}
                        </span>
                        {row.active === false && <span className="text-xs font-medium rounded-full px-2.5 py-0.5 bg-gray-100 text-gray-500" data-testid={`metric-inactive-${i}`}>Inactive</span>}
                        {dirty && <span className="text-xs text-amber-600" data-testid={`metric-dirty-${i}`}>Unsaved changes</span>}
                    </div>
                    <p className="mt-1 text-xs text-gray-500">Displays publicly as <span className="font-mono text-[color:var(--gsf-green-700)]" data-testid={`metric-preview-value-${i}`}>{displayed}</span></p>
                </div>
                <div className="flex flex-wrap gap-2">
                    <button onClick={onPreview} className="gsf-btn-outline text-xs" data-testid={`metric-preview-${i}`}><Eye size={12} /> Preview</button>
                    {row.key && !row._draft && <button onClick={onHistory} className="gsf-btn-outline text-xs" data-testid={`metric-history-${i}`}><History size={12} /> History</button>}
                </div>
            </div>

            <div className="mt-5 grid grid-cols-1 md:grid-cols-12 gap-3">
                <Field label="Metric key" required span={3} testid={`metric-key-field-${i}`} hint="lower_snake_case">
                    <input value={row.key} onChange={(e) => onChange(i, "key", e.target.value)} className="gsf-input font-mono text-xs" data-testid={`metric-key-${i}`} placeholder="e.g. total_investment" />
                    {invalidKey && <p className="mt-1 text-[11px] text-red-600" data-testid={`metric-key-error-${i}`}>Must be 2–40 lowercase letters/digits/underscore.</p>}
                </Field>
                <Field label="Public title" span={5} testid={`metric-title-field-${i}`}>
                    <input value={row.title} onChange={(e) => onChange(i, "title", e.target.value)} className="gsf-input" data-testid={`metric-title-${i}`} maxLength={120} placeholder="e.g. Total Community Investment" />
                </Field>
                <Field label="Display order" span={2} testid={`metric-order-field-${i}`}>
                    <input type="number" min={0} value={row.order} onChange={(e) => onChange(i, "order", parseInt(e.target.value, 10) || 0)} className="gsf-input" data-testid={`metric-order-${i}`} />
                </Field>
                <Field label="Active" span={2} testid={`metric-active-field-${i}`} hint="Uncheck to hide">
                    <label className="inline-flex items-center gap-2 mt-2 text-sm">
                        <input type="checkbox" checked={row.active !== false} onChange={(e) => onChange(i, "active", e.target.checked)} data-testid={`metric-active-${i}`} />
                        {row.active !== false ? "Active" : "Inactive"}
                    </label>
                </Field>

                <Field label="Prefix" span={2} testid={`metric-prefix-field-${i}`}>
                    <select value={row.prefix || ""} onChange={(e) => onChange(i, "prefix", e.target.value)} className="gsf-input" data-testid={`metric-prefix-${i}`}>
                        {PREFIX_OPTIONS.map((p) => <option key={`p-${p}`} value={p}>{p || "(none)"}</option>)}
                    </select>
                </Field>
                <Field label="Numeric value" required span={3} testid={`metric-value-field-${i}`}>
                    <input type="number" inputMode="numeric" pattern="[0-9]*" min={0} value={row.value}
                        onChange={(e) => onChange(i, "value", Math.max(0, parseInt(e.target.value, 10) || 0))}
                        className="gsf-input font-mono tabular-nums" style={{ minWidth: "6.5rem" }} data-testid={`metric-value-${i}`} />
                </Field>
                <Field label="Suffix" span={2} testid={`metric-suffix-field-${i}`}>
                    <select value={row.suffix || ""} onChange={(e) => onChange(i, "suffix", e.target.value)} className="gsf-input" data-testid={`metric-suffix-${i}`}>
                        {SUFFIX_OPTIONS.map((p) => <option key={`s-${p}`} value={p}>{p || "(none)"}</option>)}
                    </select>
                </Field>
                <Field label="Short label (fallback)" span={5} testid={`metric-label-field-${i}`}>
                    <input value={row.label} onChange={(e) => onChange(i, "label", e.target.value)} className="gsf-input" data-testid={`metric-label-${i}`} maxLength={120} />
                </Field>

                <Field label="Description" span={12} testid={`metric-description-field-${i}`}
                    hint={`${(row.description || "").length}/600 characters`}>
                    <textarea rows={3} value={row.description}
                        onChange={(e) => onChange(i, "description", e.target.value.slice(0, 600))}
                        className="gsf-input" data-testid={`metric-description-${i}`} maxLength={600} />
                </Field>

                <Field label="Change note (optional, added to audit trail)" span={12} testid={`metric-note-field-${i}`}>
                    <input value={note} onChange={(e) => setNote(e.target.value.slice(0, 400))} className="gsf-input" data-testid={`metric-note-${i}`} placeholder="e.g. Q1 2026 reporting refresh" maxLength={400} />
                </Field>
            </div>

            <div className="mt-5 flex flex-wrap items-center justify-between gap-3 border-t border-gray-100 pt-4">
                <div className="text-xs text-gray-500 space-x-3" data-testid={`metric-audit-${i}`}>
                    {row.updated_at && <span>Last updated {new Date(row.updated_at).toLocaleString()}</span>}
                    {row.updated_by && <span>· by <span className="font-medium text-gray-700">{row.updated_by}</span></span>}
                </div>
                <div className="flex flex-wrap gap-2">
                    <button onClick={onCancel} disabled={busy} className="gsf-btn-outline text-sm" data-testid={`metric-cancel-${i}`}>
                        <Undo2 size={14} /> Cancel
                    </button>
                    <button onClick={() => onSaveDraft(note)} disabled={busy} className="gsf-btn-outline text-sm" data-testid={`metric-save-draft-${i}`}>
                        <Save size={14} /> Save as Draft
                    </button>
                    <button onClick={() => onPublish(note)} disabled={busy} className="gsf-btn-primary text-sm" data-testid={`metric-publish-${i}`}>
                        <Send size={14} /> Publish
                    </button>
                </div>
            </div>
        </div>
    );
}

function Field({ label, hint, required, span = 6, testid, children }) {
    return (
        <div className={`md:col-span-${span}`} data-testid={testid}>
            <label className="block text-xs font-medium text-gray-600 mb-1">
                {label}{required && <span className="text-red-500">&nbsp;*</span>}
            </label>
            {children}
            {hint && <p className="mt-1 text-[11px] text-gray-500">{hint}</p>}
        </div>
    );
}

function PreviewModal({ metric, onClose }) {
    const displayed = `${metric.prefix || ""}${Number(metric.value || 0).toLocaleString()}${metric.suffix || ""}`;
    return (
        <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4" onClick={onClose} data-testid="metric-preview-modal">
            <div className="bg-white rounded-2xl max-w-xl w-full p-8 relative" onClick={(e) => e.stopPropagation()}>
                <button onClick={onClose} className="absolute top-4 right-4 text-gray-400 hover:text-gray-700" data-testid="metric-preview-close"><X size={18} /></button>
                <p className="text-xs uppercase tracking-widest text-gray-500 mb-1">Live preview · Community Impact card</p>
                <h3 className="font-serif text-xl text-[color:var(--gsf-green-700)] font-semibold">{metric.title || metric.label}</h3>
                {/* Preview mimics the public card styling — same colours, borders and typography. */}
                <div className="mt-6 rounded-2xl bg-[color:var(--gsf-green-900)] text-white p-8 border border-white/10" data-testid="metric-preview-card">
                    <div className="text-4xl sm:text-5xl font-serif font-bold text-[color:var(--gsf-gold-500)]" data-testid="metric-preview-number">{displayed}</div>
                    <div className="mt-3 text-sm text-white/85 leading-snug">{metric.title || metric.label}</div>
                </div>
                {metric.description && <p className="mt-4 text-sm text-gray-600" data-testid="metric-preview-description">{metric.description}</p>}
                <p className="mt-4 text-xs text-amber-700" data-testid="metric-preview-note">
                    {metric.verified ? "This is the value visitors currently see." : "This is a Draft — public visitors still see the last Published value until you click Publish."}
                </p>
            </div>
        </div>
    );
}

function HistoryModal({ payload, onClose }) {
    const { row, history } = payload;
    return (
        <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4" onClick={onClose} data-testid="metric-history-modal">
            <div className="bg-white rounded-2xl max-w-2xl w-full p-8 max-h-[85vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
                <div className="flex items-start justify-between gap-3">
                    <div>
                        <p className="text-xs uppercase tracking-widest text-gray-500 mb-1">Audit trail</p>
                        <h3 className="font-serif text-xl text-[color:var(--gsf-green-700)] font-semibold">{row.title || row.label || row.key}</h3>
                        <p className="text-xs text-gray-500 mt-1 font-mono">key: {row.key}</p>
                    </div>
                    <button onClick={onClose} className="text-gray-400 hover:text-gray-700" data-testid="metric-history-close"><X size={18} /></button>
                </div>
                <div className="mt-6 space-y-3">
                    {history.length === 0 && <p className="text-sm text-gray-500" data-testid="metric-history-empty">No changes recorded yet.</p>}
                    {history.map((h, k) => (
                        <div key={k} className="border border-gray-100 rounded-lg p-4 text-sm" data-testid={`metric-history-entry-${k}`}>
                            <div className="flex flex-wrap justify-between text-xs text-gray-500">
                                <span><strong className="text-[color:var(--gsf-green-700)] capitalize">{h.action}</strong> by {h.actor || "system"}</span>
                                <span>{new Date(h.at).toLocaleString()} · {h.status}</span>
                            </div>
                            {h.previous && h.new && (
                                <div className="mt-2 grid grid-cols-2 gap-3 text-xs">
                                    <div>
                                        <div className="text-gray-400">Previous</div>
                                        <pre className="mt-1 bg-gray-50 rounded p-2 font-mono overflow-x-auto">{JSON.stringify(h.previous, null, 2)}</pre>
                                    </div>
                                    <div>
                                        <div className="text-gray-400">New</div>
                                        <pre className="mt-1 bg-[color:var(--gsf-soft-green)] rounded p-2 font-mono overflow-x-auto">{JSON.stringify(h.new, null, 2)}</pre>
                                    </div>
                                </div>
                            )}
                            {h.note && <p className="mt-2 text-xs italic text-gray-600">Note: {h.note}</p>}
                        </div>
                    ))}
                </div>
            </div>
        </div>
    );
}
